import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { WebsocketService } from "app/service/websocket.service";
import { CamManageService } from "app/main/camera-manage/cam-manage.service";
import { ToastService } from "app/service/toast.service";
import { CoreConfigService } from "@core/services/config.service";

interface NotificationItem {
  id: string;
  heading: string;
  text: string;
  icon: string;
  time: string;
  read?: boolean;
}

interface NotificationData {
  messages: any[];
  systemMessages: NotificationItem[];
  system: boolean;
}

@Injectable({
  providedIn: "root",
})
export class NotificationsService {
  public apiData = [];
  public onApiDataChange: BehaviorSubject<any>;

  private notifications: NotificationData = {
    messages: [],
    systemMessages: [],
    system: true
  };
  private cameras: any[] = [];
  private offlineCameras = new Set<string>();
  private cameraStatusSub: any = null;
  private offlineToasts = new Map<string, number>();
  private appName = "";

  constructor(
    private _httpClient: HttpClient,
    private _websocketService: WebsocketService,
    private _camManageService: CamManageService,
    private _toastService: ToastService,
    private _coreConfigService: CoreConfigService
  ) {
    this.loadInitialNotifications();
    this.onApiDataChange = new BehaviorSubject<any>(this.notifications);
    this._coreConfigService.config.subscribe((config) => {
      this.appName = config?.app?.appName || "";
      if (this.appName === "E1-VMS") {
        if (!this.cameraStatusSub) {
          this.subscribeToCameraStatus();
        }
      } else {
        if (this.cameraStatusSub) {
          this.cameraStatusSub.unsubscribe();
          this.cameraStatusSub = null;
        }
      }
    });
    this.loadCameras();
  }

  private loadInitialNotifications() {
    try {
      const stored = localStorage.getItem("camera_notifications");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && Array.isArray(parsed.systemMessages)) {
          this.notifications.systemMessages = parsed.systemMessages;
        }
      }
    } catch (e) {
      console.warn("Failed to load notifications from localStorage", e);
    }
  }

  private saveNotifications() {
    try {
      localStorage.setItem("camera_notifications", JSON.stringify(this.notifications));
    } catch (e) {
      console.warn("Failed to save notifications to localStorage", e);
    }
  }

  loadCameras(): void {
    this._camManageService.getAllCam().subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.cameras = Array.isArray(res.data) ? res.data : (res.data.data || []);
          this.cameras.forEach(cam => {
            if (cam.status === "OFFLINE" && cam.code) {
              this.offlineCameras.add(cam.code);
            }
          });
        }
      },
      error: (err) => {
        console.error("Failed to load cameras in NotificationsService", err);
      }
    });
  }

  subscribeToCameraStatus(): void {
    if (this.cameraStatusSub) {
      this.cameraStatusSub.unsubscribe();
      this.cameraStatusSub = null;
    }

    if (this.appName !== "E1-VMS") {
      return;
    }

    this.cameraStatusSub = this._websocketService.connect("/topic/camera.status").subscribe({
      next: (res: any) => {
        console.log("[NotificationsService] Received camera status update", res);
        if (res && res.cameraCode) {
          const isOnline = res.status === "ONLINE";
          
          if (!isOnline) {
            // Find the camera name
            const cam = this.cameras.find(c => c.code === res.cameraCode || c.id === res.cameraCode);
            const camName = cam ? cam.name : res.cameraCode;
            
            // Add to offline tracking set
            this.offlineCameras.add(res.cameraCode);

            const now = new Date();
            const timeString = `${now.toLocaleTimeString('vi-VN')} ${now.toLocaleDateString('vi-VN')}`;

            // Camera disconnected
            const newNotification: NotificationItem = {
              id: `${res.cameraCode}-offline-${Date.now()}`,
              heading: `<span class="font-weight-bolder text-danger">Mất kết nối Camera 🔴</span>`,
              text: `Camera [${camName}] đã bị ngắt kết nối vào lúc ${timeString}.`,
              icon: 'x',
              time: timeString,
              read: false
            };
            
            this.notifications.systemMessages.unshift(newNotification);
            if (this.notifications.systemMessages.length > 20) {
              this.notifications.systemMessages = this.notifications.systemMessages.slice(0, 20);
            }
            this.saveNotifications();
            this.onApiDataChange.next({ ...this.notifications });
            
            // Trigger global error toast (only for main E1-VMS app)
            if (this.appName === "E1-VMS") {
              const activeToast = this._toastService.error(
                `Camera [${camName}] đã bị ngắt kết nối vào lúc ${timeString}!`,
                "Cảnh báo kết nối",
                { timeOut: 5000 }
              );
              if (activeToast && activeToast.toastId) {
                this.offlineToasts.set(res.cameraCode, activeToast.toastId);
              }
            }
          } else {
            // Camera went online - check if it was previously offline
            if (this.offlineCameras.has(res.cameraCode)) {
              this.offlineCameras.delete(res.cameraCode); // Remove from offline tracking

              // Find the camera name
              const cam = this.cameras.find(c => c.code === res.cameraCode || c.id === res.cameraCode);
              const camName = cam ? cam.name : res.cameraCode;

              const now = new Date();
              const timeString = `${now.toLocaleTimeString('vi-VN')} ${now.toLocaleDateString('vi-VN')}`;

              // Camera reconnected notification
              const newNotification: NotificationItem = {
                id: `${res.cameraCode}-online-${Date.now()}`,
                heading: `<span class="font-weight-bolder text-success">Camera kết nối lại 🟢</span>`,
                text: `Camera [${camName}] đã kết nối thành công trở lại vào lúc ${timeString}.`,
                icon: 'check',
                time: timeString,
                read: false
              };
              
              this.notifications.systemMessages.unshift(newNotification);
              if (this.notifications.systemMessages.length > 20) {
                this.notifications.systemMessages = this.notifications.systemMessages.slice(0, 20);
              }
              this.saveNotifications();
              this.onApiDataChange.next({ ...this.notifications });
              
              // Remove the offline toast if it exists
              const toastId = this.offlineToasts.get(res.cameraCode);
              if (toastId) {
                this._toastService.clear(toastId);
                this.offlineToasts.delete(res.cameraCode);
              }

              // Trigger global success toast (only for main E1-VMS app)
              if (this.appName === "E1-VMS") {
                this._toastService.success(
                  `Camera [${camName}] đã kết nối thành công trở lại vào lúc ${timeString}.`,
                  "Khôi phục kết nối"
                );
              }
            }
          }
        }
      },
      error: (err) => {
        console.error("[NotificationsService] WebSocket error in camera status topic", err);
        // Automatically reconnect after 5 seconds
        setTimeout(() => this.subscribeToCameraStatus(), 5000);
      },
      complete: () => {
        console.warn("[NotificationsService] WebSocket subscription completed. Re-subscribing...");
        // Automatically reconnect after 5 seconds
        setTimeout(() => this.subscribeToCameraStatus(), 5000);
      }
    });
  }

  markAllAsRead(): void {
    let hasUnread = false;
    if (Array.isArray(this.notifications.systemMessages)) {
      this.notifications.systemMessages.forEach(item => {
        if (!item.read) {
          item.read = true;
          hasUnread = true;
        }
      });
    }
    if (Array.isArray(this.notifications.messages)) {
      this.notifications.messages.forEach(item => {
        if (!item.read) {
          item.read = true;
          hasUnread = true;
        }
      });
    }
    if (hasUnread) {
      this.saveNotifications();
      this.onApiDataChange.next({ ...this.notifications });
    }
  }

  getNotificationsData(): Promise<any[]> {
    return Promise.resolve(this.apiData);
  }
}
